import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, Eye, Play, RefreshCw, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

const POLL_INTERVAL_MS = 3000;
const SAMPLE_DISPLAY_CAP = 50;

// Mirrors DrivePathAuditPanel.jsx's useJobPolling, parameterised by which
// diagnosticsAPI job-status method to poll -- kept local to this pair of
// sections rather than shared, matching this codebase's existing
// convention of one copy per feature (drivePathAudit*, pennylaneInvoiceFiles*
// each already roll their own).
function useJobPolling(statusFn, { onCompleted, onFailed, onProgress }) {
  const [polling, setPolling] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const pollRef = useRef(null);
  const elapsedRef = useRef(null);
  const startTimeRef = useRef(null);

  const stop = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (elapsedRef.current) {
      clearInterval(elapsedRef.current);
      elapsedRef.current = null;
    }
    startTimeRef.current = null;
    setPolling(false);
  }, []);

  useEffect(() => stop, [stop]);

  const start = useCallback((id) => {
    stop();
    setPolling(true);
    setElapsed(0);
    startTimeRef.current = Date.now();

    elapsedRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);

    pollRef.current = setInterval(async () => {
      try {
        const { data } = await statusFn(id);
        if (data.status === 'completed') {
          stop();
          onCompleted(data.result);
        } else if (data.status === 'failed') {
          stop();
          onFailed(data.error, data.progress || null);
        } else if (data.status === 'running') {
          onProgress(data.progress || null);
        }
      } catch (err) {
        stop();
        toast.error(getErrorMessage(err));
      }
    }, POLL_INTERVAL_MS);
  }, [stop, statusFn, onCompleted, onFailed, onProgress]);

  return { polling, elapsed, start };
}

function ProgressBadge({ progress }) {
  if (!progress) return null;
  if (progress.phase === 'fetching_live') {
    return (
      <Badge className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
        <Loader2 className="h-3 w-3 animate-spin mr-1 inline" />
        REFETCHING LIVE -- {fmtInt(progress.connections_scanned)} of {fmtInt(progress.connections_total)} connections,
        {' '}{fmtInt(progress.legs_fetched)} legs fetched
      </Badge>
    );
  }
  if (progress.phase === 'classifying') {
    return (
      <Badge className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
        <Loader2 className="h-3 w-3 animate-spin mr-1 inline" />
        CLASSIFYING -- {fmtInt(progress.scanned)} of {fmtInt(progress.total_scanned)} scanned,
        {' '}{fmtInt(progress.candidates_found)} candidates found
      </Badge>
    );
  }
  if (progress.phase === 'applying') {
    return (
      <Badge className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
        <Loader2 className="h-3 w-3 animate-spin mr-1 inline" />
        APPLYING -- {fmtInt(progress.scanned)} of {fmtInt(progress.total_rows ?? progress.total_candidates)} processed,
        {' '}{fmtInt(progress.written)} written &middot; {fmtInt(progress.skipped_conflict)} skipped
      </Badge>
    );
  }
  return null;
}

function TypeCensus({ typeCensus }) {
  if (!typeCensus || Object.keys(typeCensus).length === 0) return null;
  const rows = Object.entries(typeCensus).sort((a, b) => b[1].count - a[1].count);
  return (
    <details open data-testid="revolut-cp-backfill-type-census">
      <summary className="text-sm font-medium cursor-pointer">
        Type census ({rows.length} type{rows.length === 1 ? '' : 's'}) -- confirms/kills the per-type parsing rule
      </summary>
      <div className="mt-2 overflow-x-auto border rounded-lg max-h-72 overflow-y-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b text-left text-muted-foreground">
              <th className="px-2 py-1">Type</th>
              <th className="px-2 py-1">Count</th>
              <th className="px-2 py-1">Would parse</th>
              <th className="px-2 py-1">Sample descriptions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([type, info]) => (
              <tr key={type} className="border-b last:border-0 align-top">
                <td className="px-2 py-1 font-mono whitespace-nowrap">{type}</td>
                <td className="px-2 py-1 tabular-nums">{fmtInt(info.count)}</td>
                <td className="px-2 py-1 tabular-nums">{fmtInt(info.would_parse_count)}</td>
                <td className="px-2 py-1 text-muted-foreground">
                  {(info.samples || []).map((sample, i) => (
                    <div key={i} className="truncate max-w-xs">{sample}</div>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

function ParseTagCounts({ tagCounts }) {
  if (!tagCounts || Object.keys(tagCounts).length === 0) return null;
  return (
    <div className="overflow-x-auto border rounded-lg">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b bg-muted/30">
            <th className="px-2 py-1 text-left">Outcome (parse tag)</th>
            <th className="px-2 py-1 text-right">Count</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(tagCounts).map(([tag, count]) => (
            <tr key={tag} className="border-b last:border-0">
              <td className="px-2 py-1 font-mono">{tag}</td>
              <td className="px-2 py-1 text-right tabular-nums">{fmtInt(count)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OutcomeCounts({ outcomeCounts }) {
  if (!outcomeCounts) return null;
  return (
    <div className="grid grid-cols-3 gap-2">
      <div className="border rounded-lg p-2 text-center border-green-200 dark:border-green-800">
        <p className="text-xs text-muted-foreground">Parsed</p>
        <p className="text-lg font-semibold tabular-nums text-green-600">{fmtInt(outcomeCounts.parsed)}</p>
      </div>
      <div className="border rounded-lg p-2 text-center border-amber-200 dark:border-amber-800">
        <p className="text-xs text-muted-foreground">Left null (no pattern)</p>
        <p className="text-lg font-semibold tabular-nums text-amber-600">{fmtInt(outcomeCounts.no_pattern)}</p>
      </div>
      <div className="border rounded-lg p-2 text-center">
        <p className="text-xs text-muted-foreground">Unchanged (no live leg)</p>
        <p className="text-lg font-semibold tabular-nums">{fmtInt(outcomeCounts.no_live_leg_found)}</p>
      </div>
    </div>
  );
}

export function RevolutCounterpartyBackfillSection() {
  const [dispatching, setDispatching] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewProgress, setPreviewProgress] = useState(null);
  const [previewFailed, setPreviewFailed] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [applyProgress, setApplyProgress] = useState(null);
  const [applyFailed, setApplyFailed] = useState(null);

  const previewPoll = useJobPolling(diagnosticsAPI.revolutCounterpartyBackfillJobStatus, {
    onCompleted: useCallback((result) => {
      setPreviewProgress(null);
      setPreviewFailed(null);
      setPreview(result);
      toast.success('Revolut counterparty preview complete');
    }, []),
    onFailed: useCallback((error, lastProgress) => {
      setPreviewProgress((prev) => lastProgress ?? prev);
      setPreviewFailed(error);
      toast.error(error ?? 'preview failed');
    }, []),
    onProgress: useCallback((progress) => setPreviewProgress(progress), []),
  });

  const applyPoll = useJobPolling(diagnosticsAPI.revolutCounterpartyBackfillJobStatus, {
    onCompleted: useCallback((result) => {
      setApplyProgress(null);
      setApplyFailed(null);
      setApplyResult(result);
      setPreview(null);
      toast.success(
        result?.applied
          ? `Revolut backfill applied: ${result?.written ?? 0} written, ${result?.skipped_conflict ?? 0} skipped`
          : `Revolut backfill unavailable: ${result?.reason ?? 'no connection'}`
      );
    }, []),
    onFailed: useCallback((error, lastProgress) => {
      setApplyProgress((prev) => lastProgress ?? prev);
      setApplyFailed(error);
      toast.error(error ?? 'apply failed');
    }, []),
    onProgress: useCallback((progress) => setApplyProgress(progress), []),
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.revolutCounterpartyBackfillJobLatest('preview');
        const job = data?.job;
        if (!job) return;
        if (job.status === 'completed' && job.result) {
          setPreview(job.result);
        } else if (job.status === 'running') {
          setPreviewProgress(job.progress || null);
          previewPoll.start(job._id);
        } else if (job.status === 'failed') {
          setPreviewProgress(job.progress || null);
          setPreviewFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.revolutCounterpartyBackfillJobLatest('apply');
        const job = data?.job;
        if (!job) return;
        if (job.status === 'completed' && job.result) {
          setApplyResult(job.result);
        } else if (job.status === 'running') {
          setApplyProgress(job.progress || null);
          applyPoll.start(job._id);
        } else if (job.status === 'failed') {
          setApplyProgress(job.progress || null);
          setApplyFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runPreview = async () => {
    setDispatching(true);
    setPreview(null);
    setPreviewProgress(null);
    setPreviewFailed(null);
    try {
      const { data } = await diagnosticsAPI.revolutCounterpartyBackfillPreviewStart();
      previewPoll.start(data.job_id);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDispatching(false);
    }
  };

  const runApply = async () => {
    const count = preview ? preview.total_candidates : null;
    if (!window.confirm(
      count != null
        ? `This will backfill counterparty on ${count} Revolut transaction(s) from a live leg-description refetch. Continue?`
        : 'This will backfill counterparty on any Revolut transactions a live leg-description refetch resolves. Continue?'
    )) return;

    setDispatching(true);
    setApplyResult(null);
    setApplyProgress(null);
    setApplyFailed(null);
    try {
      const { data } = await diagnosticsAPI.revolutCounterpartyBackfillApplyStart();
      applyPoll.start(data.job_id);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDispatching(false);
    }
  };

  const previewPolling = previewPoll.polling;
  const applyPolling = applyPoll.polling;
  const busy = dispatching || previewPolling || applyPolling;

  return (
    <div className="border rounded-lg p-3 space-y-3" data-testid="revolut-counterparty-backfill-section">
      <div>
        <p className="text-sm font-medium">Revolut Counterparty Backfill (historical)</p>
        <p className="text-[11px] text-muted-foreground mt-0.5">
          Live-refetches every connected Revolut account's transactions and re-derives counterparty
          from each leg's description for historical rows still carrying a null/empty counterparty.
          Preview live-checks without writing; Apply re-checks fresh and writes, skipping any row a
          concurrent live sync has already corrected. Runs as a background job (live-refetch over
          thousands of rows exceeds a synchronous request).
        </p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Button onClick={runPreview} disabled={busy} size="sm" data-testid="revolut-cp-backfill-preview">
          {previewPolling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
          Preview
        </Button>

        <Button
          onClick={runApply}
          disabled={busy}
          size="sm"
          variant={preview && preview.total_candidates > 0 ? 'default' : 'outline'}
          data-testid="revolut-cp-backfill-apply"
        >
          {applyPolling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
          {preview ? `Apply (${fmtInt(preview.total_candidates)} candidates)` : 'Apply'}
        </Button>

        {previewPolling && (
          <span className="text-xs text-muted-foreground tabular-nums flex items-center gap-1">
            <RefreshCw className="h-3 w-3 animate-spin" />
            preview {previewPoll.elapsed}s
          </span>
        )}
        {applyPolling && (
          <span className="text-xs text-muted-foreground tabular-nums flex items-center gap-1">
            <RefreshCw className="h-3 w-3 animate-spin" />
            apply {applyPoll.elapsed}s
          </span>
        )}
      </div>

      {previewPolling && previewProgress && (
        <div className="space-y-2" data-testid="revolut-cp-backfill-preview-progress">
          <ProgressBadge progress={previewProgress} />
        </div>
      )}

      {applyPolling && applyProgress && (
        <div className="space-y-2" data-testid="revolut-cp-backfill-apply-progress">
          <ProgressBadge progress={applyProgress} />
        </div>
      )}

      {preview && (
        <div className="space-y-3">
          {preview.available === false ? (
            <div className="border-2 rounded-lg p-2 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/20 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
              <p className="text-xs text-amber-700 dark:text-amber-400">Not available: {preview.reason}</p>
            </div>
          ) : (
            <>
              <Badge className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                PREVIEW (read-only) -- {fmtInt(preview.total_scanned)} scanned, {fmtInt(preview.total_candidates)} candidates
              </Badge>
              <TypeCensus typeCensus={preview.type_census} />
              <OutcomeCounts outcomeCounts={preview.outcome_counts} />
              <ParseTagCounts tagCounts={preview.parse_tag_counts} />
              {preview.connection_errors && preview.connection_errors.length > 0 && (
                <p className="text-[11px] text-red-600">
                  {preview.connection_errors.length} connection error(s): {preview.connection_errors.map((e) => e.error).join('; ')}
                </p>
              )}
              {preview.proposed_corrections && preview.proposed_corrections.length > 0 && (
                <details>
                  <summary className="text-sm font-medium cursor-pointer">
                    Proposed corrections ({preview.proposed_corrections.length}
                    {preview.proposed_corrections.length > SAMPLE_DISPLAY_CAP ? `, showing first ${SAMPLE_DISPLAY_CAP}` : ''})
                  </summary>
                  <div className="mt-2 overflow-x-auto border rounded-lg max-h-64 overflow-y-auto">
                    <table className="w-full text-xs">
                      <tbody>
                        {preview.proposed_corrections.slice(0, SAMPLE_DISPLAY_CAP).map((c, i) => (
                          <tr key={c.transaction_id || i} className="border-b last:border-0">
                            <td className="px-2 py-1 font-mono whitespace-nowrap">{c.transaction_id}</td>
                            <td className="px-2 py-1">{c.new_counterparty}</td>
                            <td className="px-2 py-1 text-muted-foreground">{c.leg_description}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}
              <p className="text-[10px] text-muted-foreground">Generated: {preview.generated_at}</p>
            </>
          )}
        </div>
      )}

      {previewFailed && (
        <div className="space-y-1">
          <Badge className="text-[10px] bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300">PREVIEW FAILED</Badge>
          <p className="text-xs text-red-600">{previewFailed}</p>
        </div>
      )}

      {applyFailed && (
        <div className="space-y-1">
          <Badge className="text-[10px] bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300">APPLY FAILED</Badge>
          <p className="text-xs text-red-600">{applyFailed}</p>
        </div>
      )}

      {applyResult && (
        <div className="space-y-3">
          {applyResult.applied === false ? (
            <div className="border-2 rounded-lg p-2 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/20 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
              <p className="text-xs text-amber-700 dark:text-amber-400">Not applied: {applyResult.reason}</p>
            </div>
          ) : (
            <>
              <Badge className="text-[10px] bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
                APPLIED -- {fmtInt(applyResult.written)} written, {fmtInt(applyResult.skipped_conflict)} skipped (conflict)
              </Badge>
              <OutcomeCounts outcomeCounts={applyResult.outcome_counts} />
              <ParseTagCounts tagCounts={applyResult.parse_tag_counts} />
              {applyResult.written_samples && applyResult.written_samples.length > 0 && (
                <details>
                  <summary className="text-sm font-medium cursor-pointer">
                    Written samples ({applyResult.written_samples.length})
                  </summary>
                  <div className="mt-2 overflow-x-auto border rounded-lg max-h-48 overflow-y-auto">
                    <table className="w-full text-xs">
                      <tbody>
                        {applyResult.written_samples.map((s, i) => (
                          <tr key={s.transaction_id || i} className="border-b last:border-0">
                            <td className="px-2 py-1 font-mono">{s.transaction_id}</td>
                            <td className="px-2 py-1">{s.new_counterparty}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}
              <p className="text-[10px] text-muted-foreground">Run: {applyResult.run_id} &middot; Generated: {applyResult.generated_at}</p>
            </>
          )}
        </div>
      )}
    </div>
  );
}
